import { useState } from "react";
import { formatDateNumeric } from "../../utils/formatters";
import {
  Stack,
  Card,
  Badge,
  Group,
  Text,
  Select,
  Alert,
  Loader,
  ActionIcon,
  Tooltip,
  Paper,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconInfoCircle,
  IconEye,
  IconFileText,
  IconDownload,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { extractApiError } from "../../utils/formErrorHandler";
import { useFacilityContext } from "../../hooks/useFacilityContext";
import { useFacilities } from "../../hooks/useFacilities";
import { useQuery } from "@tanstack/react-query";
import api from "../../lib/apiClient";

/**
 * US-139: one row per audit SESSION, with real finding counts. This used to be
 * one row per access grant with every number produced by Math.random() on the
 * server — a "compliance score" of 70-100 that changed on every refresh.
 *
 * `compliance_score` is gone: nothing in the schema models a score. An absent
 * field is honest; an invented one is a fabricated compliance record.
 *
 * US-140: the report IS now persisted, so the download action is back — gated on
 * `report_available` rather than the old hardcoded-null `report_url`. The S3 key
 * never reaches the client; the download route mints a short-lived signed URL.
 */
interface AuditReport {
  report_id: string;
  session_name: string;
  audit_type: string;
  auditor_name: string;
  auditor_email: string;
  auditor_organization: string;
  facility_id: string;
  facility_name: string;
  audit_date: string;
  /** The session's real lifecycle state (audit_sessions.status). */
  status: string;
  overall_rating: string | null;
  findings_count: number;
  critical_findings: number;
  recommendations_count: number;
  /** US-140: whether a generated report is stored and downloadable. */
  report_available: boolean;
  report_generated_at: string | null;
}

/**
 * Audit Reports Tab
 * Shows completed audit reports and findings
 */
export function AuditReportsTab() {
  const { currentFacilityId, facilityIds } = useFacilityContext();
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>(
    currentFacilityId || facilityIds[0] || "",
  );
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  /**
   * US-140 (AC17): fetch a short-lived signed URL, then open it. Deliberately an
   * authenticated `api.get` rather than pointing window.location at the route —
   * a bare navigation carries no Bearer token and the download would 401.
   */
  const handleDownload = async (report: AuditReport) => {
    setDownloadingId(report.report_id);
    try {
      const { data } = await api.get<{ data: { url: string } }>(
        `/facilities/${report.facility_id}/audit-reports/${report.report_id}/download`,
      );
      window.open(data.data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      notifications.show({
        title: "Download failed",
        message: extractApiError(error, "Could not retrieve the audit report"),
        color: "red",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  // Fetch facilities to get names
  const { data: facilities } = useFacilities();

  const {
    data: reports,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["audit-reports", selectedFacilityId, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);

      const { data } = await api.get(
        `/facilities/${selectedFacilityId}/audit-reports?${params.toString()}`,
      );
      return data.data as AuditReport[];
    },
    enabled: !!selectedFacilityId,
  });

  // US-139: the real audit_sessions lifecycle. The previous
  // draft/submitted/reviewed vocabulary matched nothing in the schema — the API
  // hardcoded every row to "submitted", so the filter and these colours were
  // decorative.
  const getStatusColor = (status: string) => {
    switch (status) {
      case "in_progress":
        return "blue";
      case "report_generated":
        return "yellow";
      case "completed":
        return "green";
      default:
        return "gray";
    }
  };

  const formatStatus = (status: string) => status.replace(/_/g, " ");

  const getSeverityColor = (count: number) => {
    if (count === 0) return "green";
    if (count <= 2) return "yellow";
    return "red";
  };

  // Create facility options with names
  const facilityOptions =
    facilities
      ?.filter((f) => facilityIds.includes(f.id))
      .map((f) => ({ value: f.id, label: f.name })) || [];

  return (
    <Stack gap="md">
      {/* Info Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        View completed audit reports, findings, and recommendations. Reports are
        submitted by auditors after completing their audits.
      </Alert>

      {/* Filters */}
      <Card withBorder>
        <Group>
          {facilityIds.length > 1 && (
            <Select
              label="Facility"
              placeholder="Select facility"
              data={facilityOptions}
              value={selectedFacilityId}
              onChange={(value) =>
                setSelectedFacilityId(value || facilityIds[0])
              }
              style={{ flex: 1, maxWidth: 300 }}
            />
          )}
          <Select
            label="Status"
            placeholder="All statuses"
            data={[
              { value: "", label: "All" },
              { value: "in_progress", label: "In Progress" },
              { value: "report_generated", label: "Report Generated" },
              { value: "completed", label: "Completed" },
            ]}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value || "")}
            clearable
            style={{ flex: 1, maxWidth: 300 }}
          />
        </Group>
      </Card>

      {/* Reports Table */}
      <Card withBorder>
        {isLoading ? (
          <Stack align="center" py="xl">
            <Loader size="md" />
            <Text c="dimmed">Loading audit reports...</Text>
          </Stack>
        ) : isError ? (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Failed to load audit reports. Please try refreshing the page.
          </Alert>
        ) : !reports || reports.length === 0 ? (
          <Stack align="center" py="xl" gap="xs">
            <IconFileText size={48} stroke={1.5} color="gray" />
            <Text c="dimmed">No audit reports available</Text>
            <Text size="sm" c="dimmed">
              Completed audit reports will appear here after auditors submit
              their findings
            </Text>
          </Stack>
        ) : (
          <>
            <Stack gap="md">
              {reports.map((report) => (
                <Paper key={report.report_id} withBorder p="md">
                  <Group justify="space-between" mb="md">
                    <div>
                      <Group gap="xs" mb="xs">
                        <Title order={5}>{report.auditor_organization}</Title>
                        <Badge color={getStatusColor(report.status)}>
                          {formatStatus(report.status)}
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed">
                        {report.auditor_name || report.auditor_email} •{" "}
                        {report.audit_type.replace("_", " ")}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {formatDateNumeric(report.audit_date)}
                      </Text>
                    </div>
                    <Group gap="xs">
                      <Tooltip label="View report details" withArrow>
                        <ActionIcon variant="light" color="blue" size="lg">
                          <IconEye size={20} />
                        </ActionIcon>
                      </Tooltip>
                      {/*
                        US-140: restored. The backend persists the generated PDF
                        to the audit bucket and hands back a 5-minute signed URL
                        on request — the key itself never reaches the client.
                        Absent when the auditor has not generated a report yet,
                        rather than rendered-but-dead.
                      */}
                      {report.report_available && (
                        <Tooltip
                          label={
                            report.report_generated_at
                              ? `Download report (generated ${formatDateNumeric(report.report_generated_at)})`
                              : "Download report"
                          }
                          withArrow
                        >
                          <ActionIcon
                            variant="light"
                            color="teal"
                            size="lg"
                            loading={downloadingId === report.report_id}
                            onClick={() => void handleDownload(report)}
                          >
                            <IconDownload size={20} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Group>

                  <Group grow>
                    <Paper withBorder p="sm">
                      <Text size="xs" c="dimmed" mb={4}>
                        Findings
                      </Text>
                      <Group gap="xs">
                        <Text size="lg" fw={600}>
                          {report.findings_count}
                        </Text>
                        <Badge
                          color={getSeverityColor(report.critical_findings)}
                          size="sm"
                          variant="light"
                        >
                          {report.critical_findings} critical
                        </Badge>
                      </Group>
                    </Paper>

                    <Paper withBorder p="sm">
                      <Text size="xs" c="dimmed" mb={4}>
                        Recommendations
                      </Text>
                      <Text size="lg" fw={600}>
                        {report.recommendations_count}
                      </Text>
                    </Paper>

                    {/*
                      US-139: the "Compliance Score" tile is gone. It rendered a
                      server-side Math.random()*30+70 as a percentage with an
                      Excellent/Good/Needs-Improvement badge — a fabricated
                      regulatory score a manager could act on, that changed on
                      every refresh. Nothing in the schema models a compliance
                      score, so there is nothing truthful to show here.
                      `overall_rating` is the auditor's own real verdict.
                    */}
                    {report.overall_rating && (
                      <Paper withBorder p="sm">
                        <Text size="xs" c="dimmed" mb={4}>
                          Overall Rating
                        </Text>
                        <Badge size="sm" variant="light">
                          {report.overall_rating.replace(/_/g, " ")}
                        </Badge>
                      </Paper>
                    )}
                  </Group>
                </Paper>
              ))}
            </Stack>

            {/* Summary */}
            <Card withBorder mt="md">
              <Group>
                <Text size="sm" c="dimmed">
                  Total Reports: {reports.length}
                </Text>
                <Text size="sm" c="dimmed">
                  •
                </Text>
                <Text size="sm" c="dimmed">
                  Completed:{" "}
                  {reports.filter((r) => r.status === "completed").length}
                </Text>
                <Text size="sm" c="dimmed">
                  •
                </Text>
                <Text size="sm" c="dimmed">
                  Critical Findings:{" "}
                  {reports.reduce((sum, r) => sum + r.critical_findings, 0)}
                </Text>
              </Group>
            </Card>
          </>
        )}
      </Card>
    </Stack>
  );
}
